import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface UpdateProductParams {
  productId: string;
  field: string;
  value: any;
}

export const useProductEditor = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateProduct = useMutation({
    mutationFn: async ({ productId, field, value }: UpdateProductParams) => {
      const { data, error } = await supabase
        .from('products')
        .update({ 
          [field]: value,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('slug', productId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Product not found');
      return data;
    },
    onSuccess: (data, variables) => {
      // Immediately set the query data to force UI update
      queryClient.setQueryData(['product', variables.productId], (oldData: any) => {
        if (oldData) {
          return { ...oldData, [variables.field]: variables.value };
        }
        return oldData;
      });
      
      // Then invalidate and refetch for fresh data from server
      queryClient.invalidateQueries({ queryKey: ['product', variables.productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.refetchQueries({ queryKey: ['product', variables.productId], type: 'active' });
      
      toast.success('Changes saved successfully');
    },
    onError: (error: any) => {
      console.error('Error updating product:', error);
      toast.error('Failed to save changes: ' + error.message);
    },
  });

  const publishProduct = useMutation({
    mutationFn: async (productId: string) => {
      const { data, error } = await supabase
        .from('products')
        .update({ 
          is_published: true,
          updated_by: user?.id,
          updated_at: new Date().toISOString(),
        })
        .eq('slug', productId)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Product not found');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product published successfully');
    },
    onError: (error: any) => {
      console.error('Error publishing product:', error);
      toast.error('Failed to publish: ' + error.message);
    },
  });

  const deleteProduct = useMutation({
    mutationFn: async (productId: string) => {
      // First get the product UUID from slug
      const { data: product } = await supabase
        .from('products')
        .select('id')
        .eq('slug', productId)
        .maybeSingle();
      
      if (!product) throw new Error('Product not found');
      
      // Delete related data first (to avoid foreign key violations)
      await supabase.from('product_plans').delete().eq('product_id', product.id);
      await supabase.from('product_qa').delete().eq('product_id', product.id);
      await supabase.from('product_reviews').delete().eq('product_id', product.id);
      await supabase.from('service_features').delete().eq('product_id', product.id);
      
      // Finally delete the product
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('slug', productId);
      
      if (error) throw error;
      return productId;
    },
    onSuccess: (productId) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      toast.success('Product deleted successfully');
    },
    onError: (error: any) => {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product: ' + error.message);
    },
  });

  return {
    updateProduct: updateProduct.mutate,
    publishProduct: publishProduct.mutate,
    deleteProduct: deleteProduct.mutate,
    isUpdating: updateProduct.isPending,
    isPublishing: publishProduct.isPending,
    isDeleting: deleteProduct.isPending,
  };
};
